import React from 'react';
import css from '../SelfHostedModal.scss';
import { SelfHostedModalLayout } from '../SelfHostedModalLayout';
import { Trans, useTranslation } from '@wix/wix-i18n-config';
import { useSelfHostedContext } from '../SelfHostedModalContextProvider';
import { Heading, Text, TextButton } from '@wix/design-system';
import { ENV_VARIABLE_DOC, ROUTES } from '../../../../../constants/routes';
import { DataHooks } from '../../../../../constants/dataHooks';
import { NumberedSnippet } from '../components/NumberedSnippet/NumberedSnippet';
import type { SelfHostedModalBiParams } from '../types';
import { useTemplatesPageBi } from '../../../../../hooks/useTemplatesPageBi';
import { DeploymentHostType } from '@wix/ambassador-devcenter-apps-v1-app-template/types';

const translationPath = 'app.template-page.modal.self-hosted.step3';
const biParams: SelfHostedModalBiParams = {
  stepName: 'test-your-app-step',
  mainCTAName: 'go to dev center',
  secondaryCTAName: 'close',
};

export const TestYourAppStep = () => {
  const { t } = useTranslation();
  const {
    formData: { appDetails, selectedDeploymentProvider },
  } = useSelfHostedContext();
  const { onClose } = useSelfHostedContext();
  const { reportAppTestingDocsClicked } = useTemplatesPageBi();

  const onAppTestingDocsClicked = () => {
    reportAppTestingDocsClicked(appDetails?.appId);
    window.open(ROUTES.appChecksAndTestingDocs);
  };

  return (
    <SelfHostedModalLayout
      className={css.testYourApp}
      biParams={biParams}
      primaryButtonText={t(`${translationPath}.primary-cta`)}
      secondaryButtonText={t(`${translationPath}.secondary-cta`)}
      primaryButtonOnClick={() => {
        window.open(ROUTES.devCenterExtensionsPage(appDetails?.appId || ''));
      }}
      secondaryButtonOnClick={onClose}
    >
      <Heading size="small" dataHook={DataHooks.SelfHostModalTestYourAppTitle}>
        {t(`${translationPath}.title`)}
      </Heading>
      <Text dataHook={DataHooks.SelfHostModalTestYourAppEnvVarText}>
        <Trans
          i18nKey={`${translationPath}.desc`}
          values={{
            devVars: t(
              `${translationPath}.env-variables-${selectedDeploymentProvider?.toLowerCase()}`,
            ),
          }}
          components={[
            selectedDeploymentProvider ===
            DeploymentHostType.UNKNOWN_DEPLOYMENT_HOST_TYPE ? (
              <Text weight="bold" />
            ) : (
              <TextButton
                dataHook={DataHooks.EnvVariablesLink}
                onClick={() => {
                  window.open(
                    ENV_VARIABLE_DOC[selectedDeploymentProvider || ''],
                  );
                }}
              />
            ),
          ]}
        />
      </Text>
      <NumberedSnippet
        className={css.codeBlock}
        headerText={t(`${translationPath}.snippet`)}
        appId={appDetails?.appId || ''}
        appSecret={appDetails?.appSecret || ''}
        appPublicKey={appDetails?.appPublicKey || ''}
      />
      <Heading size="small">{t(`${translationPath}.test-your-app`)}</Heading>
      <Text>
        <Trans i18nKey={`${translationPath}.test-your-app-description`}>
          <TextButton
            dataHook="app-testing-link"
            onClick={onAppTestingDocsClicked}
          />
        </Trans>
      </Text>
    </SelfHostedModalLayout>
  );
};
